// =============================================================================
// useLeadStory — fetch the dramatized hero for a fixture.
// GET /feed/{slug}/match/{eventId}/lead-story  → { leadStory | null }
// Self-quiet: returns null when the tenant has insights off or there's no
// priced gem to lead with, so the HeroLeadCard simply doesn't render.
// Dramatization Engine, Phase 3 (docs/DRAMATIZATION-ENGINE.md).
// =============================================================================

import { useEffect, useState } from 'react';
import { useEngineClient } from '../api/EngineClientProvider.js';

export interface LeadStoryOdds {
  label: string;
  /** Decimal odds — always rendered with "." (European odds rule). */
  price: number;
}

/**
 * Story archetype — the shape of the narrative the engine dramatized
 * ('streak', 'duel', 'wonderkid', 'verdict', 'form'). `label` is already
 * localized by the engine; render it verbatim.
 */
export interface LeadStoryArchetype {
  id: 'streak' | 'duel' | 'wonderkid' | 'verdict' | 'form';
  label: string;
}

/**
 * A secondary "Did you know?" support item — a smaller dramatized angle
 * with its own themed odds. Powers the DidYouKnowRail beneath the hero.
 * All copy (`hook`, `marketLabel`, odds `label`) is already localized and
 * display-ready (prices use "."); render verbatim, never add tip language.
 */
export interface LeadStorySupport {
  hook: string;
  marketLabel: string;
  odds: LeadStoryOdds[];
  archetype: LeadStoryArchetype;
}

export interface LeadStory {
  headline: string;
  body: string;
  device: string;
  marketLabel: string;
  odds: LeadStoryOdds[];
  subject: string;
  dramaScore: number;
  // Dramatization Engine P4 — NEW. Older cached payloads omit these, so
  // both are optional and consumers must no-op gracefully when absent.
  /** The narrative archetype of the lead story (badge on the hero). */
  archetype?: LeadStoryArchetype;
  /** Secondary "Did you know?" angles (the support rail). */
  support?: LeadStorySupport[];
}

export function useLeadStory(eventId: string): { leadStory: LeadStory | null; loading: boolean } {
  const client = useEngineClient();
  const slug = client.slug;
  const [leadStory, setLeadStory] = useState<LeadStory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setLeadStory(null);
    const url = `/onsite/feed/${encodeURIComponent(slug)}/match/${encodeURIComponent(eventId)}/lead-story`;
    fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl.signal, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { leadStory?: LeadStory | null } | null) => {
        if (ctrl.signal.aborted) return;
        setLeadStory(d?.leadStory ?? null);
        setLoading(false);
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return (): void => ctrl.abort();
  }, [eventId, slug]);

  return { leadStory, loading };
}
